import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { confirmDialog } from 'primereact/confirmdialog';
import { classNames } from 'primereact/utils';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  ActivityTypeDTO,
  ScoreTemplateOptionDTO,
  ProfessionalWithSpecialitiesDTO,
  ProfessionalSpecialityDTO,
  LocationDTO,
  ProfessionalEntry,
} from '../types/spontaneous.types';
import {
  getActivityTypes,
  getScoreTemplates,
  searchProfessionals,
  getLocations,
  getCurrentProfessional,
  validatePatientScheduled,
  validateLocationOverlaps,
  validateProfessionalOverlaps,
  bookSpontaneous,
} from '../services/SpontaneousService';
import { registerArrival, callPatient } from '../services/AgendaService';
import { parseApiError } from '../types/error.types';
import PatientSelector, { PatientSelectorValue } from './PatientSelector';
import { hasSenderPermissionByTaskMessageTypeAndMessageType } from '../../../servicesHooks/DigitalRP/useDigitalRPService';
import { EMessageType, ETaskMessageType } from '../../Recipe/RecipeEnums';
import { FieldError } from '../../../components/novarium/NvTableParts';
import './OutOfScheduleScreen.scss';

interface OutOfScheduleScreenProps {
  onClose: () => void;
  onBooked: () => void;
  onError?: (message: string) => void;
  initialPatientId?: number | null;
}

type DateMode = 'today' | 'other';
type TimeMode = 'now' | 'pick';

const TIME_IN_PAST_MESSAGE = 'Elegí una hora a partir de la actual';

/** Pie del modal de decisión: "Cancelar" (secundario, con el foco: PrimeReact 9 no tiene defaultFocus) y la acción. */
function confirmFooter(acceptLabel: string) {
  return ({ accept, reject }: { accept: () => void; reject: () => void }) => (
    <>
      <Button type="button" className="buttonMain2" outlined label="Cancelar" onClick={reject} autoFocus />
      <Button type="button" className="buttonMain" label={acceptLabel} onClick={accept} />
    </>
  );
}

/** Decisión que bloquea el avance (notificaciones.md §3): resuelve true si elige seguir. */
function askToContinue(header: string, message: string, acceptLabel: string): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    confirmDialog({
      header,
      message,
      className: 'nv-scope nv-confirm',
      footer: confirmFooter(acceptLabel),
      accept: () => resolve(true),
      reject: () => resolve(false),
    });
  });
}

export default function OutOfScheduleScreen({
  onClose,
  onBooked,
  onError,
  initialPatientId,
}: OutOfScheduleScreenProps) {
  // ─── Patient ────────────────────────────
  const [patientValue, setPatientValue] = useState<PatientSelectorValue>({ mode: 'identify', data: null });
  const [patientResetKey, setPatientResetKey] = useState(0);
  const selectedPatient = patientValue.mode === 'identify' ? patientValue.data?.patient ?? null : null;

  // ─── Appointment config ─────────────────
  const [dateMode, setDateMode] = useState<DateMode>('today');
  const [otherDate, setOtherDate] = useState<Date | null>(null);
  const [timeMode, setTimeMode] = useState<TimeMode>('now');
  const [pickedTime, setPickedTime] = useState<Date | null>(() => { const d = new Date(); d.setHours(13, 0, 0, 0); return d; });
  const [duration, setDuration] = useState<number>(15);
  const [activityTypes, setActivityTypes] = useState<ActivityTypeDTO[]>([]);
  const [selectedActivityType, setSelectedActivityType] = useState<number | null>(null);
  const [scoreTemplates, setScoreTemplates] = useState<ScoreTemplateOptionDTO[]>([]);
  const [selectedScoreTemplate, setSelectedScoreTemplate] = useState<number | null>(null);
  const [reason, setReason] = useState('');

  // ─── Professionals ──────────────────────
  const [professionalOptions, setProfessionalOptions] = useState<ProfessionalWithSpecialitiesDTO[]>([]);
  const [selectedProfessional, setSelectedProfessional] = useState<ProfessionalWithSpecialitiesDTO | null>(null);
  const [selectedSpeciality, setSelectedSpeciality] = useState<ProfessionalSpecialityDTO | null>(null);
  const [professionalEntries, setProfessionalEntries] = useState<ProfessionalEntry[]>([]);

  // ─── Locations ──────────────────────────
  const [locationOptions, setLocationOptions] = useState<LocationDTO[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<LocationDTO | null>(null);
  const [locationEntries, setLocationEntries] = useState<LocationDTO[]>([]);

  // ─── Notifications ─────────────────────
  const [notifyWhatsApp, setNotifyWhatsApp] = useState(true);
  const [notifyEmail, setNotifyEmail] = useState(true);
  const [disabledWhatsApp, setDisabledWhatsApp] = useState(false);
  const [disabledEmail, setDisabledEmail] = useState(false);

  // ─── Submission ─────────────────────────
  const [submitting, setSubmitting] = useState(false);
  const [bookedAppointmentId, setBookedAppointmentId] = useState<number | null>(null);
  const [attendLoading, setAttendLoading] = useState(false);

  // ─── Init on mount ──────────────────────
  useEffect(() => {
    setPatientResetKey((k) => k + 1);

    getActivityTypes().then((types) => {
      setActivityTypes(types);
      if (types.length > 0) setSelectedActivityType(types[0].id);
    }).catch(console.error);

    getScoreTemplates().then((templates) => {
      setScoreTemplates(templates);
      if (templates.length > 0) setSelectedScoreTemplate(templates[0].id);
    }).catch(console.error);

    getLocations().then(setLocationOptions).catch(console.error);

    // Preload current user as professional
    getCurrentProfessional().then((prof) => {
      if (!prof) return;
      searchProfessionals(prof.name).then((results) => {
        const match = results.find((r) => r.id === prof.id);
        if (match && match.specialities.length > 0) {
          const spec = match.specialities[0];
          setProfessionalEntries([{
            professionalId: match.id,
            professionalName: match.name,
            specialityId: spec.id,
            specialityName: spec.name,
          }]);
        }
      });
    }).catch(console.error);

    // Check sender permissions for notifications
    Promise.all([
      hasSenderPermissionByTaskMessageTypeAndMessageType(EMessageType.WHATSAPP, ETaskMessageType.APPOINTMENTS),
      hasSenderPermissionByTaskMessageTypeAndMessageType(EMessageType.EMAIL, ETaskMessageType.APPOINTMENTS),
    ]).then(([waDto, emailDto]) => {
      const waEnabled = !!waDto?.hassenderpermission;
      const emailEnabled = !!emailDto?.hassenderpermission;
      setDisabledWhatsApp(!waEnabled);
      setDisabledEmail(!emailEnabled);
      if (!waEnabled) setNotifyWhatsApp(false);
      if (!emailEnabled) setNotifyEmail(false);
    }).catch(() => {
      setDisabledWhatsApp(true);
      setDisabledEmail(true);
      setNotifyWhatsApp(false);
      setNotifyEmail(false);
    });
  }, []);

  // Auto-select speciality when professional has only one
  useEffect(() => {
    if (selectedProfessional?.specialities.length === 1) {
      setSelectedSpeciality(selectedProfessional.specialities[0]);
    } else {
      setSelectedSpeciality(null);
    }
  }, [selectedProfessional]);

  const handleAddProfessional = useCallback(() => {
    if (!selectedProfessional || !selectedSpeciality) return;
    const isDuplicate = professionalEntries.some(
      (e) => e.professionalId === selectedProfessional.id && e.specialityId === selectedSpeciality.id,
    );
    if (isDuplicate) return;
    setProfessionalEntries((prev) => [...prev, {
      professionalId: selectedProfessional.id,
      professionalName: selectedProfessional.name,
      specialityId: selectedSpeciality.id,
      specialityName: selectedSpeciality.name,
    }]);
    setSelectedProfessional(null);
    setSelectedSpeciality(null);
  }, [selectedProfessional, selectedSpeciality, professionalEntries]);

  const handleAddLocation = useCallback(() => {
    if (!selectedLocation) return;
    if (locationEntries.some((l) => l.id === selectedLocation.id)) return;
    setLocationEntries((prev) => [...prev, selectedLocation]);
    setSelectedLocation(null);
  }, [selectedLocation, locationEntries]);

  // ─── Computed values ────────────────────
  const effectiveDate = dateMode === 'today' ? format(new Date(), 'yyyy-MM-dd') : (otherDate ? format(otherDate, 'yyyy-MM-dd') : null);
  const effectiveTime = (() => {
    if (dateMode === 'today' && timeMode === 'now') return format(new Date(), 'HH:mm');
    if (pickedTime) return format(pickedTime, 'HH:mm');
    return null;
  })();

  // ─── Validation ─────────────────────────
  const formError = useMemo((): string | null => {
    if (dateMode === 'other' && !otherDate) return 'Completá la fecha';
    if ((dateMode === 'other' || timeMode === 'pick') && !pickedTime) return 'Completá la hora';
    if (dateMode === 'today' && timeMode === 'pick' && pickedTime) {
      const now = new Date();
      if (pickedTime.getHours() < now.getHours() || (pickedTime.getHours() === now.getHours() && pickedTime.getMinutes() < now.getMinutes())) {
        return TIME_IN_PAST_MESSAGE;
      }
    }
    if (professionalEntries.length === 0) return 'Agregá al menos un profesional';
    if (locationEntries.length === 0) return 'Agregá al menos una ubicación';
    return null;
  }, [dateMode, otherDate, timeMode, pickedTime, professionalEntries.length, locationEntries.length]);

  const canConfirmPatient = patientValue.mode === 'identify'
    ? !!patientValue.data
    : !!(patientValue.data.patientReference.trim() && patientValue.data.patientPhone.trim());

  const canConfirm = !formError && canConfirmPatient && !submitting;

  // Por qué "Confirmar turno" está deshabilitado (tooltip del botón)
  const confirmBlockedReason = canConfirm || submitting
    ? null
    : formError ?? (patientValue.mode === 'identify'
      ? 'Elegí un paciente'
      : 'Completá el nombre de referencia y el teléfono del paciente');
  const timeInPast = formError === TIME_IN_PAST_MESSAGE;
  const canAddProfessional = !!selectedProfessional && !!selectedSpeciality;
  const canAddLocation = !!selectedLocation;

  // ─── Booking ────────────────────────────
  const handleConfirm = useCallback(async () => {
    // El botón ya está deshabilitado con formError como motivo (tooltip)
    if (formError) return;
    if (!effectiveDate || !effectiveTime) return;

    // Run validations before confirming
    const profIds = professionalEntries.map((e) => e.professionalId);
    const locIds = locationEntries.map((l) => l.id);

    try {
      // 1. Patient already scheduled?
      const scheduled = selectedPatient
        ? await validatePatientScheduled(profIds, selectedPatient.id, effectiveDate)
        : { alreadyScheduled: false };
      if (scheduled.alreadyScheduled) {
        const confirmed = await askToContinue(
          '¿Asignar otro turno al paciente?',
          'El paciente ya tiene un turno ese día con alguno de los profesionales elegidos.',
          'Asignar de todas formas',
        );
        if (!confirmed) return;
      }

      // 2. Location overlaps?
      const locOverlaps = await validateLocationOverlaps({
        appointmentDate: effectiveDate, fromTime: effectiveTime, duration, locationIds: locIds,
      });
      if (locOverlaps.hasOverlaps) {
        const confirmed = await askToContinue(
          '¿Usar un consultorio ocupado?',
          'Alguno de los consultorios elegidos ya tiene un turno en ese horario.',
          'Usar de todas formas',
        );
        if (!confirmed) return;
      }

      // 3. Professional overlaps?
      const profOverlaps = await validateProfessionalOverlaps({
        appointmentDate: effectiveDate, fromTime: effectiveTime, duration, professionalIds: profIds,
      });
      if (profOverlaps.hasOverlaps) {
        const confirmed = await askToContinue(
          '¿Continuar con horarios superpuestos?',
          'Los profesionales elegidos tienen horarios superpuestos en esa fecha.',
          'Continuar',
        );
        if (!confirmed) return;
      }
    } catch (err) {
      onError?.(parseApiError(err, 'No se pudo validar el turno. Intentá de nuevo.'));
      return;
    }

    // Final confirmation
    const when = dateMode === 'today'
      ? (timeMode === 'now' ? 'ahora' : `hoy a las ${effectiveTime}`)
      : `el ${format(new Date(`${effectiveDate}T00:00:00`), 'EEE d MMM', { locale: es })} a las ${effectiveTime}`;
    confirmDialog({
      header: '¿Confirmar el turno fuera de agenda?',
      message: `El turno se va a agendar para ${when}.`,
      className: 'nv-scope nv-confirm',
      footer: confirmFooter('Confirmar turno'),
      accept: async () => {
        let patientPayload: Record<string, unknown>;
        if (patientValue.mode === 'identify') {
          if (!patientValue.data) return;
          patientPayload = { patientId: patientValue.data.patientId };
        } else {
          const u = patientValue.data;
          if (!u.patientReference.trim() || !u.patientPhone.trim()) return;
          patientPayload = {
            patientReference: u.patientReference,
            patientPhone: u.patientPhone,
            patientDocument: u.patientDocument,
            ...(u.healthEntityPlanId ? { healthEntityPlanId: u.healthEntityPlanId } : {}),
            ...(u.benefitNumber ? { benefitNumber: u.benefitNumber } : {}),
          };
        }

        setSubmitting(true);
        try {
          const response = await bookSpontaneous({
            ...patientPayload,
            professionals: professionalEntries.map((e) => ({ professionalId: e.professionalId, specialityId: e.specialityId })),
            locationIds: locationEntries.map((l) => l.id),
            activityTypeId: selectedActivityType!,
            scoreTemplateId: selectedScoreTemplate ?? null,
            appointmentDate: dateMode === 'today' ? null : effectiveDate,
            appointmentTime: dateMode === 'today' && timeMode === 'now' ? null : effectiveTime,
            duration,
            videoCall: false,
            reasonConsultation: reason,
            notifyWhatsApp,
            notifyEmail,
          });
          setBookedAppointmentId(response.appointmentId);
          onBooked();
        } catch (err) {
          // Error general: solo la card flotante (onError), sin repetirlo en la pantalla
          onError?.(parseApiError(err, 'No se pudo agendar el turno. Intentá de nuevo.'));
        } finally {
          setSubmitting(false);
        }
      },
    });
  }, [formError, patientValue, professionalEntries, locationEntries, selectedActivityType, selectedScoreTemplate, dateMode, timeMode, effectiveDate, effectiveTime, duration, reason, selectedPatient, onBooked, onError]);

  const handleAttendNow = useCallback(async () => {
    if (!bookedAppointmentId) return;
    setAttendLoading(true);
    try {
      await registerArrival(bookedAppointmentId);
      await callPatient(bookedAppointmentId);
      onClose();
    } catch (err) {
      onError?.(parseApiError(err, 'No se pudo atender al paciente. Intentá de nuevo.'));
    } finally {
      setAttendLoading(false);
    }
  }, [bookedAppointmentId, onClose, onError]);


  const isBooked = bookedAppointmentId !== null;

  return (
    <div className="nv-scope nv-page oos-screen">
      <header className="nv-page-head">
        <h1 className="nv-h1">Turno fuera de agenda</h1>
      </header>

      <div className="oos-screen__body">
        {isBooked ? (
          /* ══════════ POST-BOOKING ══════════ */
          <div className="oos-booked">
            <span className="nv-status-ok">
              <i className="pi pi-check-circle" aria-hidden="true" /> El turno fue creado
            </span>
          </div>
        ) : (
          <>
          {/* ══════════ TURNO ══════════ */}
          <section className="nv-section" aria-labelledby="oos-turn-title">
            <h2 id="oos-turn-title" className="nv-section-title">Turno</h2>

            <div className="nv-form-row">
              <div className="nv-field nv-w-s">
                <label htmlFor="oos-date-mode">Fecha <span className="nv-req">*</span></label>
                <Dropdown
                  inputId="oos-date-mode"
                  value={dateMode}
                  options={[
                    { label: 'Hoy', value: 'today' },
                    { label: 'Otra fecha', value: 'other' },
                  ]}
                  onChange={(e) => {
                    setDateMode(e.value);
                    if (e.value === 'today') setTimeMode('now');
                    else setTimeMode('pick');
                  }}
                  panelClassName="nv-scope"
                />
              </div>
              {dateMode === 'other' && (
                <div className="nv-field nv-w-s">
                  <label htmlFor="oos-other-date">Seleccionar fecha <span className="nv-req">*</span></label>
                  <Calendar
                    inputId="oos-other-date"
                    value={otherDate}
                    onChange={(e) => setOtherDate(e.value as Date)}
                    dateFormat="dd/mm/yy"
                    showIcon
                    minDate={new Date(new Date().setDate(new Date().getDate() + 1))}
                    panelClassName="nv-scope"
                  />
                </div>
              )}
              <div className="nv-field nv-w-s">
                <label htmlFor="oos-time">Hora <span className="nv-req">*</span></label>
                {dateMode === 'today' ? (
                  <Dropdown
                    inputId="oos-time"
                    value={timeMode}
                    options={[
                      { label: 'Ahora', value: 'now' },
                      { label: 'Seleccionar hora', value: 'pick' },
                    ]}
                    onChange={(e) => setTimeMode(e.value)}
                    panelClassName="nv-scope"
                  />
                ) : (
                  <Calendar
                    inputId="oos-time"
                    value={pickedTime}
                    onChange={(e) => setPickedTime(e.value as Date)}
                    timeOnly
                    hourFormat="24"
                    showIcon
                    icon="pi pi-clock"
                    placeholder="HH:mm"
                    panelClassName="nv-scope"
                  />
                )}
              </div>
              {dateMode === 'today' && timeMode === 'pick' && (
                <div className="nv-field nv-w-s">
                  <label htmlFor="oos-picked-time">Seleccionar hora <span className="nv-req">*</span></label>
                  {/* TODO(design-system): con p-invalid el borde rojo no llega al botón del reloj (.p-datepicker-trigger) */}
                  <Calendar
                    inputId="oos-picked-time"
                    value={pickedTime}
                    onChange={(e) => setPickedTime(e.value as Date)}
                    timeOnly
                    hourFormat="24"
                    showIcon
                    icon="pi pi-clock"
                    placeholder="HH:mm"
                    panelClassName="nv-scope"
                    className={timeInPast ? 'p-invalid' : undefined}
                    aria-invalid={timeInPast || undefined}
                    aria-describedby={timeInPast ? 'oos-picked-time-error' : undefined}
                  />
                  <FieldError id="oos-picked-time-error" message={timeInPast ? TIME_IN_PAST_MESSAGE : undefined} />
                </div>
              )}
              <div className="nv-field nv-w-s">
                <label htmlFor="oos-duration">Duración (min) <span className="nv-req">*</span></label>
                <InputNumber inputId="oos-duration" value={duration} onValueChange={(e) => setDuration(e.value ?? 15)} min={1} max={99} showButtons />
              </div>
            </div>

            <div className="nv-form-row">
              <div className="nv-field nv-w-m">
                <label htmlFor="oos-template">Template por defecto</label>
                <Dropdown
                  inputId="oos-template"
                  value={selectedScoreTemplate}
                  options={scoreTemplates}
                  optionLabel="description"
                  optionValue="id"
                  onChange={(e) => setSelectedScoreTemplate(e.value)}
                  placeholder="Seleccionar"
                  panelClassName="nv-scope"
                />
              </div>
              <div className="nv-field nv-w-m">
                <label htmlFor="oos-activity">Tipo de actividad <span className="nv-req">*</span></label>
                <Dropdown
                  inputId="oos-activity"
                  value={selectedActivityType}
                  options={activityTypes}
                  optionLabel="name"
                  optionValue="id"
                  onChange={(e) => setSelectedActivityType(e.value)}
                  placeholder="Seleccionar"
                  panelClassName="nv-scope"
                />
              </div>
              <div className="nv-field nv-w-m">
                <label htmlFor="oos-reason">Motivo de consulta</label>
                <InputText
                  id="oos-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Ej.: control, dolor de cabeza"
                />
              </div>
            </div>
          </section>

          {/* ══════════ PROFESIONALES ══════════ */}
          <section className="nv-section" aria-labelledby="oos-prof-title">
            <h2 id="oos-prof-title" className="nv-section-title">Profesionales <span className="nv-req">*</span></h2>

            <div className="nv-form-row">
              <div className="nv-field nv-w-m">
                <label htmlFor="oos-professional">Profesional</label>
                <Dropdown
                  inputId="oos-professional"
                  value={selectedProfessional}
                  options={professionalOptions}
                  optionLabel="name"
                  filter
                  filterPlaceholder="Buscar profesional"
                  onFilter={(e) => { searchProfessionals(e.filter).then(setProfessionalOptions).catch(console.error); }}
                  onChange={(e) => setSelectedProfessional(e.value)}
                  placeholder="Seleccionar"
                  emptyMessage="Escribí para buscar"
                  panelClassName="nv-scope"
                />
              </div>
              <div className="nv-field nv-w-m">
                <label htmlFor="oos-speciality">Especialidad</label>
                <Dropdown
                  inputId="oos-speciality"
                  value={selectedSpeciality}
                  options={selectedProfessional?.specialities ?? []}
                  optionLabel="name"
                  onChange={(e) => setSelectedSpeciality(e.value)}
                  placeholder={selectedProfessional ? 'Seleccionar' : 'Elegí un profesional primero'}
                  disabled={!selectedProfessional}
                  panelClassName="nv-scope"
                />
              </div>
              <div className="nv-field-inline-action">
                {/* El motivo de deshabilitado va en el span: un botón deshabilitado no recibe el mouse */}
                <span
                  className={classNames('nv-tip', { 'nv-disabled-tip': !canAddProfessional })}
                  data-pr-tooltip={canAddProfessional ? 'Agregar profesional' : 'Elegí un profesional y su especialidad primero'}
                  tabIndex={canAddProfessional ? undefined : 0}
                >
                  <Button
                    type="button"
                    text
                    className="nv-icon"
                    icon="pi pi-plus-circle"
                    aria-label="Agregar profesional"
                    onClick={handleAddProfessional}
                    disabled={!canAddProfessional}
                  />
                </span>
              </div>
            </div>
            {professionalEntries.length > 0 && (
              <div className="oos-grid">
                <div className="oos-grid__header">
                  <span className="oos-grid__col">Profesional</span>
                  <span className="oos-grid__col">Especialidad</span>
                  <span className="oos-grid__actions"><span className="nv-sr-only">Acciones</span></span>
                </div>
                {professionalEntries.map((entry, idx) => (
                  <div key={`${entry.professionalId}-${entry.specialityId}`} className="oos-grid__row">
                    <span className="oos-grid__col">{entry.professionalName}</span>
                    <span className="oos-grid__col">{entry.specialityName}</span>
                    <span className="oos-grid__actions">
                      <Button
                        type="button"
                        text
                        className="nv-icon"
                        icon="pi pi-trash"
                        aria-label={`Quitar ${entry.professionalName}`}
                        tooltip="Quitar"
                        tooltipOptions={{ className: 'nv-tooltip', event: 'both', position: 'top' }}
                        onClick={() => setProfessionalEntries((prev) => prev.filter((_, i) => i !== idx))}
                      />
                    </span>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* ══════════ UBICACIONES ══════════ */}
          <section className="nv-section" aria-labelledby="oos-loc-title">
            <h2 id="oos-loc-title" className="nv-section-title">Ubicaciones <span className="nv-req">*</span></h2>

            <div className="nv-form-row">
              <div className="nv-field nv-w-m">
                <label htmlFor="oos-location">Consultorio</label>
                <Dropdown
                  inputId="oos-location"
                  value={selectedLocation}
                  options={locationOptions}
                  optionLabel="name"
                  filter
                  filterPlaceholder="Buscar"
                  onChange={(e) => setSelectedLocation(e.value)}
                  placeholder="Seleccionar"
                  emptyMessage="Sin resultados"
                  panelClassName="nv-scope"
                />
              </div>
              <div className="nv-field-inline-action">
                <span
                  className={classNames('nv-tip', { 'nv-disabled-tip': !canAddLocation })}
                  data-pr-tooltip={canAddLocation ? 'Agregar ubicación' : 'Elegí un consultorio primero'}
                  tabIndex={canAddLocation ? undefined : 0}
                >
                  <Button
                    type="button"
                    text
                    className="nv-icon"
                    icon="pi pi-plus-circle"
                    aria-label="Agregar ubicación"
                    onClick={handleAddLocation}
                    disabled={!canAddLocation}
                  />
                </span>
              </div>
            </div>
            {locationEntries.length > 0 && (
              <div className="oos-grid">
                <div className="oos-grid__header">
                  <span className="oos-grid__col">Ubicación</span>
                  <span className="oos-grid__actions"><span className="nv-sr-only">Acciones</span></span>
                </div>
                {locationEntries.map((loc, idx) => (
                  <div key={loc.id} className="oos-grid__row">
                    <span className="oos-grid__col">{loc.name}</span>
                    <span className="oos-grid__actions">
                      <Button
                        type="button"
                        text
                        className="nv-icon"
                        icon="pi pi-trash"
                        aria-label={`Quitar ${loc.name}`}
                        tooltip="Quitar"
                        tooltipOptions={{ className: 'nv-tooltip', event: 'both', position: 'top' }}
                        onClick={() => setLocationEntries((prev) => prev.filter((_, i) => i !== idx))}
                      />
                    </span>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* ══════════ PACIENTE ══════════ */}
          <section className="nv-section" aria-labelledby="oos-patient-title">
            <h2 id="oos-patient-title" className="nv-section-title">Paciente</h2>
            {/* Bloque propio: el selector (compartido) arma su layout en flujo normal, no como ítems del flex */}
            <div>
              <PatientSelector
                visible={true}
                initialPatientId={initialPatientId}
                showContactFields={false}
                onChange={setPatientValue}
                resetKey={patientResetKey}
              />
            </div>
          </section>

          {/* ══════════ DETALLE ══════════ */}
          <section className="nv-section" aria-labelledby="oos-detail-title">
            <h2 id="oos-detail-title" className="nv-section-title">Detalle</h2>
            <div className="nv-field" role="group" aria-labelledby="oos-notify-label">
              <label id="oos-notify-label">Notificar al paciente</label>
              {/* TODO(design-system): el Checkbox deshabilitado se ve igual que uno habilitado sin marcar */}
              <div className="oos-notify__options">
                <span
                  className="nv-check-row nv-tip"
                  data-pr-tooltip={disabledWhatsApp ? 'No hay un remitente de WhatsApp habilitado para turnos' : undefined}
                >
                  <Checkbox inputId="oos-notify-whatsapp" checked={notifyWhatsApp} onChange={(e) => setNotifyWhatsApp(e.checked ?? false)} disabled={disabledWhatsApp} />
                  <label htmlFor="oos-notify-whatsapp" className={classNames('nv-check-row', { 'nv-muted': disabledWhatsApp })}><i className="pi pi-whatsapp" aria-hidden="true" /> WhatsApp</label>
                </span>
                <span
                  className="nv-check-row nv-tip"
                  data-pr-tooltip={disabledEmail ? 'No hay un remitente de email habilitado para turnos' : undefined}
                >
                  <Checkbox inputId="oos-notify-email" checked={notifyEmail} onChange={(e) => setNotifyEmail(e.checked ?? false)} disabled={disabledEmail} />
                  <label htmlFor="oos-notify-email" className={classNames('nv-check-row', { 'nv-muted': disabledEmail })}><i className="pi pi-envelope" aria-hidden="true" /> Email</label>
                </span>
              </div>
            </div>
          </section>
          </>
        )}
      </div>

      {/* ── Pie fijo ── */}
      <footer className="nv-page-foot oos-screen__foot">
        <hr className="nv-divider" />
        <div className="nv-actions-end">
          {isBooked ? (
            <>
              <span className="nv-status-ok nv-push-left">
                <i className="pi pi-check-circle" aria-hidden="true" /> Turno confirmado
              </span>
              <Button type="button" className="buttonMain2" outlined label="Salir" onClick={onClose} />
              {dateMode === 'today' && (
                <Button
                  type="button"
                  className="buttonMain"
                  icon="pi pi-user"
                  label={attendLoading ? 'Registrando…' : 'Atender paciente'}
                  loading={attendLoading}
                  onClick={() => void handleAttendNow()}
                />
              )}
            </>
          ) : (
            <>
              <p className="nv-note nv-push-left">
                <i className="pi pi-info-circle" aria-hidden="true" />
                <span>Se validan los solapamientos al confirmar.</span>
              </p>
              <Button type="button" className="buttonMain2" outlined label="Cancelar" onClick={onClose} />
              <span
                className={classNames('oos-screen__confirm nv-tip', { 'nv-disabled-tip': confirmBlockedReason })}
                data-pr-tooltip={confirmBlockedReason ?? undefined}
                tabIndex={confirmBlockedReason ? 0 : undefined}
              >
                <Button
                  type="button"
                  className="buttonMain"
                  label={submitting ? 'Confirmando…' : 'Confirmar turno'}
                  loading={submitting}
                  disabled={!canConfirm}
                  onClick={() => void handleConfirm()}
                />
              </span>
            </>
          )}
        </div>
      </footer>
    </div>
  );
}
